import Eyebrow from "@/components/Eyebrow";
import PricingCalculator from "@/components/PricingCalculator";
import CTABanner from "@/components/CTABanner";
import { usePageMeta, useScrollReveal, SITE_URL } from "@/lib/site";
import { FileText, ScrollText, Users, Calculator, Check, ArrowRight } from "lucide-react";

const PROVIDER_REF = { "@id": `${SITE_URL}/#organization` };

const SERVICE_LD = (id, name, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/sluzby#${id}`,
  name,
  description,
  serviceType: name,
  provider: PROVIDER_REF,
  areaServed: [
    { "@type": "City", "name": "Brno" },
    { "@type": "AdministrativeArea", "name": "Jihomoravský kraj" }
  ],
  url: `${SITE_URL}/sluzby#${id}`
});

const SERVICES = [
  {
    id: "vedeni-ucetnictvi",
    icon: FileText,
    eyebrow: "Hlavní služba",
    title: "Vedení účetnictví",
    intro:
      "Plné podvojné účetnictví pro s.r.o. — od pořízení dokladů přes závěrku až po komunikaci s úřady. Všechno na jednom místě.",
    body: [
      "Zpracujeme přijaté i vydané faktury, pokladní a bankovní doklady. Měsíčně podáváme přiznání k DPH a kontrolní hlášení, sledujeme termíny a hlídáme limity, aby vám neuniklo nic, co by mělo dopad na cashflow.",
      "Na konci roku připravíme účetní závěrku — rozvahu, výkaz zisku a ztráty, přílohu — a odešleme přiznání k dani z příjmů právnických osob. V průběhu roku poskytujeme průběžné výstupy, abyste viděli, jak na tom firma reálně je.",
    ],
    bullets: [
      "Zpracování dokladů a interních dokladů",
      "DPH a kontrolní hlášení",
      "Roční účetní závěrka a daň z příjmů",
      "Komunikace s finančním úřadem",
      "Přehledy hospodaření na vyžádání",
    ],
  },
  {
    id: "danova-evidence",
    icon: ScrollText,
    eyebrow: "Pro OSVČ",
    title: "Daňová evidence",
    intro:
      "Lehčí varianta vedení čísel pro fyzické osoby a menší subjekty. Přehledně, srozumitelně, v termínu.",
    body: [
      "Vedeme deník příjmů a výdajů, evidenci majetku a závazků, případně knihu jízd. Připravíme roční přiznání k dani z příjmů fyzických osob včetně přehledů pro ČSSZ a zdravotní pojišťovnu.",
      "Pokud zvažujete přechod na účetnictví nebo paušální daň, poradíme vám, co dává v daném okamžiku větší smysl.",
    ],
    bullets: [
      "Deník příjmů a výdajů",
      "Evidence majetku, závazků a pohledávek",
      "Roční přiznání DPFO a přehledy",
      "Konzultace přechodu na účetnictví",
    ],
  },
  {
    id: "mzdy",
    icon: Users,
    eyebrow: "Mzdová agenda",
    title: "Zpracování mezd",
    intro:
      "Kompletní mzdová agenda — abyste se vy mohli soustředit na řízení lidí, ne na výpočty.",
    body: [
      "Zpracujeme měsíční mzdy, výplatní pásky, srážky a odvody. Hlásíme nástupy a výstupy, vedeme ELDP, řešíme nemoci, dovolené, exekuce a další srážky.",
      "Zastoupíme vás na ČSSZ, zdravotních pojišťovnách i finančním úřadu. Roční zúčtování daně provedeme v zákonných lhůtách bez stresu.",
    ],
    bullets: [
      "Měsíční mzdy a výplatní pásky",
      "ELDP, ČSSZ a zdravotní pojišťovny",
      "Roční zúčtování daně",
      "Nemoci, dovolené, srážky a exekuce",
    ],
  },
  {
    id: "danove-poradenstvi",
    icon: Calculator,
    eyebrow: "Strategie a optimalizace",
    title: "Daňové poradenství",
    intro:
      "Když potřebujete odborný pohled — před investicí, při expanzi, při auditu, nebo při reorganizaci.",
    body: [
      "Pomáháme s daňovou optimalizací v mezích zákona, předinvestičními kalkulacemi, podporou při auditu i mezinárodním zdaněním. Pracujeme buď průběžně, nebo ad-hoc — podle toho, co dává pro vaši situaci smysl.",
    ],
    bullets: [
      "Daňová optimalizace",
      "Předinvestiční konzultace",
      "Podpora při auditu",
      "Mezinárodní zdanění (dle potřeby)",
    ],
  },
];

const ONBOARDING = [
  { n: "01", title: "Ozvete se", desc: "Stačí krátká zpráva nebo telefon. Reagujeme rychle." },
  { n: "02", title: "Bezplatná konzultace", desc: "Poznáme se, projdeme váš provoz a navrhneme rozsah služeb." },
  { n: "03", title: "Smlouva a cena", desc: "Pevné měsíční ceny, bez skrytých položek. Vše písemně." },
  { n: "04", title: "Hladké předání", desc: "Postaráme se o převzetí dokladů od stávajícího účetního." },
];

const FAQ = [
  {
    q: "Pro koho je G&G accounting vhodná?",
    a: "Pomáháme malým a středním s.r.o., OSVČ a živnostníkům v Brně a okolí napříč obory — od služeb a obchodu přes e-shopy a řemesla až po výrobu a stavebnictví."
  },
  {
    q: "Kde sídlíte a jak probíhá spolupráce?",
    a: "Sídlíme v Brně-Slatině na adrese Hviezdoslavova 1092/4. Schůzky probíhají u nás v kanceláři, u vás ve firmě, nebo online — podle toho, co vám vyhovuje."
  },
  {
    q: "Kolik stojí vedení účetnictví?",
    a: "Cena se odvíjí od typu subjektu, počtu dokladů a počtu zaměstnanců. Orientační odhad si můžete spočítat v naší online kalkulačce. Finální cenu vždy upřesníme po nezávazné konzultaci podle konkrétních potřeb vaší firmy."
  },
  {
    q: "Jak rychle reagujete?",
    a: "Na poptávky a dotazy reagujeme obvykle do jednoho pracovního dne."
  },
  {
    q: "Postaráte se o předání od stávajícího účetního?",
    a: "Ano. Převzetí dokladů a komunikaci s předchozím účetním vyřešíme přímo my — vy se nemusíte o nic starat."
  },
  {
    q: "Nabízíte daňové poradenství?",
    a: "Ano, daňové poradenství je jednou z našich služeb — pokrývá daňovou optimalizaci, předinvestiční konzultace, podporu při auditu i mezinárodní zdanění podle potřeby."
  }
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a }
  }))
};

export default function Services() {
  usePageMeta(
    "Služby — G&G accounting",
    "Vedení účetnictví, daňová evidence, mzdy a daňové poradenství pro firmy a živnostníky v Brně a okolí.",
    {
      jsonLd: [
        ...SERVICES.map((s) => SERVICE_LD(s.id, s.title, s.intro)),
        FAQ_LD
      ]
    }
  );
  useScrollReveal();

  return (
    <div>
      <section className="px-6 md:px-12 pt-20 md:pt-28 pb-16 bg-cream">
        <div className="max-w-7xl mx-auto reveal">
          <Eyebrow>Služby</Eyebrow>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium mt-6 leading-[0.98] max-w-4xl">
            Co pro vás <span className="text-magenta">uděláme.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-muted2 leading-relaxed max-w-2xl">
            Plný účetní servis pro s.r.o. a OSVČ. Pevné ceny, jasné rozsahy, žádná překvapení.
          </p>
        </div>
      </section>

      {SERVICES.map((s, i) => {
        const Icon = s.icon;
        const reverse = i % 2 === 1;
        const bg = i % 2 === 0 ? "bg-white" : "bg-cream";
        return (
          <section key={s.id} id={s.id} className={`px-6 md:px-12 py-20 md:py-28 ${bg}`}>
            <div className={`max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <div className="lg:col-span-7 reveal">
                <div className="w-12 h-12 rounded-md bg-magenta/10 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-magenta" />
                </div>
                <Eyebrow>{s.eyebrow}</Eyebrow>
                <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
                  {s.title}
                </h2>
                <p className="mt-6 text-lg text-ink leading-relaxed">{s.intro}</p>
                <div className="mt-6 space-y-4 text-base md:text-lg text-muted2 leading-relaxed">
                  {s.body.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-5 reveal">
                <div className="bg-white border border-line rounded-md p-8 md:p-10">
                  <div className="text-xs uppercase tracking-[0.2em] text-magenta font-semibold mb-5">
                    Co je v rozsahu
                  </div>
                  <ul className="space-y-4">
                    {s.bullets.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm md:text-base text-ink">
                        <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-magenta/10 shrink-0">
                          <Check className="w-3 h-3 text-magenta" />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ONBOARDING */}
      <section className="px-6 md:px-12 py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14 reveal">
            <Eyebrow>Spolupráce</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
              Jak začneme spolupracovat.
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted2 leading-relaxed">
              Bez stresu a bez papírové byrokracie. Stačí krátká schůzka — zbytek odbavíme za vás.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-md overflow-hidden">
            {ONBOARDING.map((o, i) => (
              <div key={o.n} className="bg-white p-8 md:p-10 reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="font-display text-4xl text-magenta font-medium mb-4">{o.n}</div>
                <h3 className="font-display text-xl font-medium mb-2">{o.title}</h3>
                <p className="text-sm text-muted2 leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingCalculator />

      {/* FAQ */}
      <section id="faq" className="px-6 md:px-12 py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14 reveal">
            <Eyebrow>Časté otázky</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
              Možná vás <span className="text-magenta">zajímá.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted2 leading-relaxed">
              Nejčastější dotazy klientů z Brna a okolí. Pokud vaše otázka chybí, ozvěte se — rádi odpovíme.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-line border border-line rounded-md overflow-hidden">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className="bg-white p-8 md:p-10 reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`faq-item-${i}`}
              >
                <h3 className="font-display text-lg md:text-xl font-medium leading-snug">
                  {item.q}
                </h3>
                <p className="mt-4 text-sm md:text-base text-muted2 leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Plánujete přechod?"
        title="Rádi se postaráme i o předání od stávajícího účetního."
        text="Bez stresu pro vás — vše vyřešíme přímo s ním nebo s ní."
        primaryHref="/kontakt"
        primaryLabel="Domluvit konzultaci"
      />
    </div>
  );
}
